import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { devMockDashboardEmpty } from './dev-mock-data';
// Client data layer for the Symphony dashboards. Each dashboard renders by pointing a sandboxed iframe's
// `src` at the DashboardRenderHandler (same origin), which resolves the caller's dashboard, injects the
// widget slots, and streams the fully-assembled, HTTP-cached document. The host no longer fetches JSON and
// builds a `srcDoc` on the main thread — the browser fetches/streams/caches the document natively.
//
// Existence (empty-state) comes from that SAME render-handler resource, not a second endpoint:
// `fetchDashboardMeta` probes `renderUrl(target)` and reads its status — 204 = nothing there yet, 2xx =
// it exists (display name in the `X-Dashboard-Name` header). That is exactly the resource the iframe
// renders and the one the native apps read (iOS `SymphonyAPI.fetchDashboard`, Android `SymphonyApi.fetchDashboard`),
// so the empty-state decision can never disagree with what actually renders. A fire-and-forget
// `pingDashboardMaintenance` still nudges the harness to run its server-side repairs (empty-home
// self-heal, stuck-skeleton recovery, on-view stale-component refresh). `renderUrl` builds the iframe `src`.
//
// A target addresses a dashboard's bento (`{ dashboard }`, or `{}` for the home/catch-up dashboard) or a
// drill-in page within it (`{ dashboard, page }`).


// The DashboardRenderHandler's browser mount (same origin as orion-web — a gateway route, no app-mount
// prefix). `/<dashboard>` renders that dashboard's bento; `?page=` selects a drill-in page within it. Same
// base PageSurface points its iframe at.
const RENDER_BASE = '/_api/dashboard/v1/dashboards/render';

// `act=1`: this host carries out a card's direct write (use-card-action), so render marks the library cards
// that declare one. A host without it gets each button's chat fallback.
export const DIRECT_CARD_ACTIONS = '1';

// The main/home dashboard's slug (workspace_id unset) — the cross-workspace catch-up screen. A target with no
// `dashboard` is the home; we address it by this concrete slug (the harness's HOME_DASHBOARD_SLUG) over the
// primary `{dashboard_slug}` route binding — the same slug getRenderedPage resolves for the home today.
export const HOME_DASHBOARD_SLUG = 'catch-up';

/** Existence + labels for a dashboard target — enough to drive the empty-state and the iframe title. The
 *  document itself is rendered by the iframe `src`, not carried here. */
export interface DashboardMeta {
  name: string;
  slug: string;
}

/** Which dashboard (bento) or drill-in page to address. `dashboard` omitted = the home/catch-up dashboard. */
export interface DashboardTarget {
  dashboard?: string;
  page?: string;
}

// Module-level cache so leaving a dashboard and coming back paints instantly from the last probe instead of
// refetching; the dashboard duplexer drives a `force` reload that replaces the entry when Orion rewrites it.
const cache = new Map<string, DashboardMeta | null>();
const inflight = new Map<string, Promise<DashboardMeta | null>>();

const keyFor = (target: DashboardTarget) => `${target.dashboard ?? '__home__'}::${target.page ?? ''}`;

/** The /api/dashboard proxy query for a target — used by the fire-and-forget maintenance ping. */
function metaQuery(target: DashboardTarget): string {
  const params = new URLSearchParams();
  if (target.dashboard) {
    params.set('dashboard', target.dashboard);
  }
  if (target.page) {
    params.set('page', target.page);
  }
  params.set('meta', '1');
  return `?${params.toString()}`;
}

/** The DashboardRenderHandler URL an iframe points its `src` at for this target. `dashboard` omitted → the
 *  home (addressed by HOME_DASHBOARD_SLUG). `reload` bumps a cache-buster so a changed dashboard refetches —
 *  an unchanged document still revalidates to a 304 via its ETag. */
export function renderUrl(target: DashboardTarget = {}, reload = 0): string {
  const params = new URLSearchParams();
  params.set('act', DIRECT_CARD_ACTIONS);
  if (target.page) {
    params.set('page', target.page);
  }
  if (reload > 0) {
    params.set('_r', String(reload));
  }
  const qs = params.toString();
  const slug = target.dashboard || HOME_DASHBOARD_SLUG;
  return `${RENDER_BASE}/${encodeURIComponent(slug)}${qs ? `?${qs}` : ''}`;
}

// Fire-and-forget nudge to the harness's on-view server-side maintenance for this target — the empty-home
// self-heal, the stuck-skeleton recovery (#7105), and the on-view stale-component refresh. This is NOT the
// existence decision (that comes from the render handler, below) — it only keeps those server-side repairs
// firing on a web view, exactly as they did when the existence probe itself went through /api/dashboard.
function pingDashboardMaintenance(target: DashboardTarget): void {
  void apiFetch(`${API_BASE}/api/dashboard${metaQuery(target)}`, { credentials: 'include' }).catch(() => {});
}

/** Probe whether a dashboard exists for this target (+ its name). `null` = nothing there yet (empty state).
 *  The existence signal is the render handler's own status on the SAME resource the iframe renders and the
 *  native apps read — 204 = empty, 2xx = present (name in the `X-Dashboard-Name` header) — so the empty-state
 *  decision and the rendered document can never disagree. An empty home still self-heals server-side. */
export async function fetchDashboardMeta(
  target: DashboardTarget = {},
  opts?: { force?: boolean; warm?: boolean },
): Promise<DashboardMeta | null> {
  const key = keyFor(target);
  if (!opts?.force) {
    if (cache.has(key)) return cache.get(key) ?? null;
    const pending = inflight.get(key);
    if (pending) return pending;
  }
  const run = (async (): Promise<DashboardMeta | null> => {
    // Dev tools ▸ Mock data ▸ "Growth dashboard in a workspace" = 0, or a synthetic workspace. No ping
    // either: the maintenance nudge asks the harness to BUILD an empty dashboard, which is the opposite
    // of what someone checking the empty state wants.
    if (devMockDashboardEmpty(target)) {
      cache.set(key, null);
      return null;
    }
    pingDashboardMaintenance(target);
    // Probe the exact resource the iframe renders (`renderUrl(target)`, same-origin gateway route, cookie
    // auth) — a full GET, which also warms the HTTP cache the iframe reuses. 204 is the explicit empty
    // state; any other non-2xx is a transient/operational failure the caller retries.
    //
    // `warm` is the speculative background pre-load (DashboardHost): fetch at LOW priority so it never
    // steals connection slots from the active surface's own render + the home's API calls, and DRAIN the
    // body — an unread response body is cancelled on GC, leaving the HTTP cache entry incomplete, so
    // without this the eventual iframe re-renders from cold and the "warm" buys nothing.
    const res = await fetch(renderUrl(target), {
      credentials: 'include',
      ...(opts?.warm ? { priority: 'low' as RequestPriority } : {}),
    });
    if (res.status === 204) {
      cache.set(key, null);
      return null;
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    // The display name rides `X-Dashboard-Name`, RFC3986-percent-encoded; a missing/malformed value is
    // non-fatal (it only titles the iframe), so fall back to the raw string then to empty.
    const rawName = res.headers.get('X-Dashboard-Name') ?? '';
    let name = rawName;
    try {
      name = decodeURIComponent(rawName);
    } catch {
      /* keep the raw header value */
    }
    const result: DashboardMeta = { name, slug: target.page || target.dashboard || HOME_DASHBOARD_SLUG };
    cache.set(key, result);
    // Warm path only: pull the body to completion so the browser actually caches the document (an unread
    // body is cancelled on GC, leaving the cache entry incomplete). Fire-and-forget — `blob()` keeps the
    // stream alive until it finishes on its own, so meta still resolves off the headers and a concurrent
    // navigation into this target isn't held behind the download.
    if (opts?.warm) void res.blob().catch(() => {});
    return result;
  })().finally(() => inflight.delete(key));
  inflight.set(key, run);
  return run;
}

/** The cached meta if a probe resolved already — lets a dashboard paint instantly on re-entry
 *  (undefined = never probed, null = probed-but-empty). */
export function getCachedDashboardMeta(target: DashboardTarget = {}): DashboardMeta | null | undefined {
  return cache.get(keyFor(target));
}

/** Warm the home dashboard probe early (called on app mount) so the empty-state decision is ready by the
 *  time the user lands on `/`. Best-effort — swallows errors. */
export function prefetchDashboardMeta(target: DashboardTarget = {}): void {
  void fetchDashboardMeta(target).catch(() => {});
}

// The dashboard document is server-assembled and HTTP-cached; the FIRST render of a cold dashboard is the
// slow part (seconds — sometimes a build). Fetching the render URL in the background warms that cache, so
// the iframe paints from cache when the user actually opens it. Once-per-target; best-effort.
const renderWarmed = new Set<string>();
const renderWarming = new Map<string, Promise<void>>();

export function isDashboardRenderWarmed(target: DashboardTarget = {}): boolean {
  return renderWarmed.has(renderUrl(target));
}

/** Warm a dashboard's server-rendered document into the HTTP cache. Resolves when the warm request settles
 *  (success or failure — the caller only needs to know the render is no longer cold). */
export function prefetchDashboardRender(target: DashboardTarget = {}): Promise<void> {
  const url = renderUrl(target);
  if (renderWarmed.has(url)) return Promise.resolve();
  const existing = renderWarming.get(url);
  if (existing) return existing;
  const run = fetch(url, { credentials: 'include' })
    .then(() => undefined)
    .catch(() => undefined)
    .finally(() => {
      renderWarmed.add(url);
      renderWarming.delete(url);
    });
  renderWarming.set(url, run);
  return run;
}
